import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, BookOpen, Mic, Sparkles, Trophy, Users, Headphones, Heart,
  GraduationCap, Lightbulb, RefreshCcw, Award, User,
} from "lucide-react";
import InstallAppButton from "@/components/InstallAppButton";

const journey = [
  { icon: User, label: "Student", desc: "Begin your journey" },
  { icon: Lightbulb, label: "Understanding", desc: "Know what every ayah means" },
  { icon: Sparkles, label: "Reflection", desc: "Tadabbur — connect it to your life" },
  { icon: BookOpen, label: "Memorization", desc: "Memorize with AI feedback" },
  { icon: RefreshCcw, label: "Revision", desc: "Spaced repetition that sticks" },
  { icon: Award, label: "Completion", desc: "Certified by your teacher" },
];

const programs = [
  { icon: Sparkles, title: "Tadabbur Before Hifz", desc: "Understand the meaning, context and reflections before you memorize." },
  { icon: Mic, title: "AI Recitation Feedback", desc: "Instant, encouraging feedback on accuracy and tajweed — like a personal Qari coach." },
  { icon: Trophy, title: "Streaks, Badges, XP", desc: "Stay motivated with daily goals, leagues and friendly competitions." },
  { icon: Users, title: "Halaqa & Teachers", desc: "Join cohorts, get reviewed by your Ustadh, share notes with peers." },
  { icon: Headphones, title: "Qari Loop Player", desc: "Loop any ayah with Alafasy, Husary, Minshawi, Sudais — at your tempo." },
  { icon: BookOpen, title: "Intelligent Revision", desc: "New Memorization, Recent Review and Long-Term Revision — planned for you, every day." },
];

export default function Landing() {
  return (
    <div className="min-h-screen geo-bg">
      {/* Nav */}
      <header className="container-page py-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center justify-between gap-3 sm:contents">
          <Link to="/" className="flex items-center gap-2.5" data-testid="brand-home">
            <img src="/Hikma-Verse-Logo.png" alt="Hikmah Verses" className="h-10 w-10 rounded-xl object-cover" />
            <div>
              <div className="font-display font-semibold text-forest-700 leading-tight">Hifz with Hikmah</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground hidden sm:block">A HikmahVerses Product</div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-foreground/80">
            <a href="#program" className="hover:text-forest-700">Features</a>
            <a href="#how" className="hover:text-forest-700">How it works</a>
            <a href="#stories" className="hover:text-forest-700">Stories</a>
          </nav>
        </div>
        <div className="flex items-center gap-2 flex-wrap sm:gap-3">
          <InstallAppButton className="btn-ghost text-sm" />
          <Link to="/login" className="btn-ghost text-sm" data-testid="nav-login">Sign in</Link>
          <Link to="/login?mode=register" className="btn-primary text-sm" data-testid="nav-cta">Start Learning <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </header>

      {/* Hero */}
      <section className="container-page pt-10 pb-24 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 animate-fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sand-200 border border-sand-300 text-xs uppercase tracking-[0.18em] text-forest-700">
            <span className="h-1.5 w-1.5 rounded-full bg-gold-500" />
            The Flagship Program of HikmahVerses
          </div>
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-forest-700 leading-[1.08] text-balance" data-testid="hero-headline">
            A World-Class Hifz Platform for the <span className="text-gold-600 italic">Modern Muslim</span>
          </h1>
          <p className="mt-6 text-lg text-foreground/75 max-w-xl leading-relaxed" data-testid="hero-subtitle">
            Understand every ayah. Reflect deeply. Memorize confidently.
            Receive AI-powered recitation feedback.
          </p>
          <p className="mt-2 text-base text-muted-foreground max-w-xl">
            Built for students, families and Tahfeez institutions.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <Link to="/login?mode=register" className="btn-primary" data-testid="hero-cta-start">
              Start Learning <ArrowRight className="h-4 w-4" />
            </Link>
            <a href="#program" className="btn-ghost" data-testid="hero-cta-explore">Explore Features</a>
          </div>
          <div className="mt-12 flex items-center gap-6 text-sm text-muted-foreground">
            <div className="flex -space-x-2">
              {["A", "Y", "M", "H"].map((c, i) => (
                <div key={c} className={`h-8 w-8 rounded-full grid place-items-center text-xs font-semibold border-2 border-sand-100 ${
                  ["bg-forest-700 text-sand-100", "bg-gold-500", "bg-terracotta-500 text-white", "bg-forest-600 text-sand-100"][i]
                }`}>{c}</div>
              ))}
            </div>
            <span>10,000+ ayahs memorized this week · across 10+ countries</span>
          </div>
        </div>

        {/* Journey illustration */}
        <div className="lg:col-span-5 relative">
          <div className="absolute -top-12 -right-6 h-72 w-72 rounded-full bg-gold-400/20 blur-3xl" />
          <div className="relative hifz-card grain p-8" data-testid="hero-journey">
            <div className="text-xs uppercase tracking-[0.22em] text-gold-600 font-semibold mb-6">Your Learning Journey</div>
            <ol className="relative space-y-0">
              {journey.map((s, i) => (
                <li key={s.label} className="relative flex gap-4 pb-6 last:pb-0">
                  {i < journey.length - 1 && (
                    <span className="absolute left-[21px] top-11 bottom-0 w-px border-l-2 border-dashed border-gold-500/50" aria-hidden="true" />
                  )}
                  <div className={`relative z-10 h-11 w-11 shrink-0 rounded-full grid place-items-center border-2 ${
                    i === journey.length - 1
                      ? "bg-gold-500 border-gold-500 text-foreground"
                      : "bg-forest-600/10 border-forest-600/30 text-forest-700"
                  }`}>
                    <s.icon className="h-5 w-5" />
                  </div>
                  <div className="pt-1">
                    <div className="font-display font-semibold text-forest-700 leading-tight">{s.label}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{s.desc}</div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* Philosophy strip */}
      <section className="container-page pb-16">
        <div className="hifz-card grain bg-forest-700 border-forest-700 text-center py-10">
          <p className="font-display text-2xl sm:text-3xl text-sand-100 text-balance">
            Don't just memorize the Qur'an.<br className="sm:hidden" />
            <span className="text-gold-400"> Understand it. Reflect on it. Live it.</span>
          </p>
        </div>
      </section>

      {/* Programs grid */}
      <section id="program" className="container-page py-16">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-[0.22em] text-gold-600 font-semibold">The Program</div>
          <h2 className="mt-2 font-display font-semibold text-3xl sm:text-4xl text-forest-700 text-balance">Understand. Reflect. Memorize. Revise.</h2>
          <p className="mt-4 text-foreground/70">
            The classical tahfiz method — new memorization, recent review, long-term revision — guided by hikmah, validated by AI.
          </p>
        </div>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {programs.map((p) => (
            <div key={p.title} className="hifz-card hifz-card-hover">
              <div className="h-11 w-11 rounded-xl bg-sand-200 grid place-items-center text-forest-700">
                <p.icon className="h-5 w-5" />
              </div>
              <h3 className="mt-5 font-display font-semibold text-xl text-forest-700">{p.title}</h3>
              <p className="mt-2 text-sm text-foreground/70 leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="container-page py-16">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-gold-600 font-semibold">How it works</div>
            <h2 className="mt-2 font-display font-semibold text-3xl sm:text-4xl text-forest-700">A daily rhythm with the Book.</h2>
            <ol className="mt-8 space-y-5">
              {[
                ["1", "Tadabbur first", "Structured pointers explain the ayah's meaning and context before you memorize."],
                ["2", "Recite into the mic", "Your AI coach listens, then scores tajweed and accuracy instantly."],
                ["3", "Follow your daily plan", "New memorization, recent review and long-term revision — balanced for you automatically."],
                ["4", "Halaqa & feedback", "Your teacher reviews recordings and leaves notes inside the app."],
              ].map(([n, t, d]) => (
                <li key={n} className="flex gap-4">
                  <div className="h-9 w-9 shrink-0 rounded-full border border-gold-500 text-gold-600 grid place-items-center font-display font-semibold">{n}</div>
                  <div>
                    <div className="font-display font-semibold text-forest-700">{t}</div>
                    <div className="text-sm text-foreground/70 mt-1 max-w-md">{d}</div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
          <div className="relative">
            <img
              src="https://images.pexels.com/photos/5973518/pexels-photo-5973518.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Islamic geometric calligraphy"
              className="rounded-2xl w-full h-[520px] object-cover border border-sand-300"
            />
          </div>
        </div>
      </section>

      {/* Stories */}
      <section id="stories" className="container-page py-16">
        <div className="text-xs uppercase tracking-[0.22em] text-gold-600 font-semibold">Stories from the Ummah</div>
        <h2 className="mt-2 font-display font-semibold text-3xl sm:text-4xl text-forest-700 max-w-2xl">Real transformations, one ayah at a time.</h2>
        <div className="mt-12 grid md:grid-cols-3 gap-5">
          {[
            { name: "Idris, 14", country: "UK", quote: "I used to memorise then forget. Now I memorise with meaning — and it sticks.", count: "120+ ayahs" },
            { name: "Aisha, 28", country: "Malaysia", quote: "Reciting into the app, getting instant tajweed feedback — like having an Ustadh in my pocket.", count: "62-day streak" },
            { name: "Ustadh Yusuf", country: "Sri Lanka", quote: "My whole halaqa is on Hifz with Hikmah. Reviewing recitations remotely saves me hours.", count: "3 cohorts" },
          ].map((s) => (
            <div key={s.name} className="hifz-card">
              <Heart className="h-4 w-4 text-terracotta-500" />
              <p className="mt-4 text-foreground/85 leading-relaxed">"{s.quote}"</p>
              <div className="mt-6 pt-4 border-t border-sand-300 flex items-center justify-between">
                <div>
                  <div className="font-display font-semibold text-forest-700">{s.name}</div>
                  <div className="text-xs text-muted-foreground">{s.country}</div>
                </div>
                <div className="text-xs font-semibold text-gold-600">{s.count}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="container-page py-20">
        <div className="hifz-card grain p-12 text-center max-w-3xl mx-auto bg-forest-700 border-forest-700 text-sand-100">
          <GraduationCap className="h-8 w-8 mx-auto text-gold-400" />
          <h2 className="mt-4 font-display font-semibold text-3xl sm:text-4xl text-sand-100 text-balance">Become a حافظ — with hikmah.</h2>
          <p className="mt-4 text-sand-100/80 max-w-xl mx-auto">Join thousands of students memorizing the Qur'an the way it deserves: with meaning, motivation and a Qari in your pocket.</p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/login?mode=register" className="btn-accent" data-testid="footer-cta-register">Start Learning</Link>
            <Link to="/login" className="btn-ghost border-sand-100/30 text-sand-100 hover:bg-sand-100/10">I already have an account</Link>
          </div>
        </div>
      </section>

      <footer className="container-page py-10 text-sm text-muted-foreground flex flex-col md:flex-row md:items-center justify-between gap-4 border-t border-sand-300">
        <div>© {new Date().getFullYear()} HikmahVerses · Hifz with Hikmah</div>
        <div>From cradle to grave — a Qur'anic life.</div>
      </footer>
    </div>
  );
}
